import { Archive, CalendarClock, FileText, Heart, Hexagon, MapPin, MessageCircle, Package, Settings, Store } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { LEGAL_PAGES } from "@gaole/shared";
import { api } from "../lib/api";
import { useAuth } from "../stores/auth";
import { PageHeader } from "../components/storefront/PageHeader";
import { ResendVerificationEmail } from "../components/ResendVerificationEmail";
import { handoffToSellerPortal } from "../lib/sellerPortal";

export function Account() {
  const navigate = useNavigate();
  const { user, config, logout, moduleEnabled } = useAuth();

  const { data: myRank } = useQuery({
    queryKey: ["my-leaderboard-rank"],
    queryFn: () =>
      api<{ entries: { rank: number; handle: string; pullCount: number }[] }>("/packs/leaderboard?period=all&limit=100"),
    enabled: moduleEnabled("packs"),
  });

  const selfEntry = myRank?.entries.find((e) => e.handle.startsWith((user?.displayName ?? "").slice(0, 2)));

  const marketplaceOn = moduleEnabled("marketplace");
  const sellerPortalUrl = config?.sellerPortalUrl?.replace(/\/$/, "");

  const { data: sellerMe } = useQuery({
    queryKey: ["marketplace-seller-me"],
    queryFn: () => api<{ profile: { status: string } | null }>("/marketplace/seller/me"),
    enabled: marketplaceOn && !!user,
  });
  const isSeller = sellerMe?.profile != null;

  function openSellerPortal() {
    if (!sellerPortalUrl) return;
    const path = isSeller ? "/" : "/information";
    if (user && handoffToSellerPortal(sellerPortalUrl, path)) return;
    window.location.href = `${sellerPortalUrl}/login`;
  }

  async function doLogout() {
    await logout();
    navigate("/");
  }

  if (!user) return null;

  return (
    <div className="space-y-5 px-4 pt-4 lg:px-6">
      <PageHeader title="Account" description="Your profile, orders, wallet, addresses, and settings." />

      <div className="glass-panel flex items-center gap-3 rounded-2xl p-4">
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-gradient-brand font-display text-lg font-bold">
          {(user.displayName ?? user.email)[0]?.toUpperCase()}
        </div>
        <div>
          <p className="font-display text-base font-bold">{user.displayName ?? "Collector"}</p>
          <p className="text-xs text-white/50">{user.email}</p>
          {!user.emailVerifiedAt && (
            <p className="text-[10px] text-amber-400">Email not verified</p>
          )}
        </div>
      </div>

      {!user.emailVerifiedAt && <ResendVerificationEmail />}

      {selfEntry && moduleEnabled("packs") && (
        <div className="glass-panel rounded-2xl p-4 text-center">
          <p className="text-[10px] uppercase tracking-widest text-white/40">All-time rips</p>
          <p className="mt-1 font-display text-2xl font-bold text-brand-pink">#{selfEntry.rank}</p>
          <p className="text-xs text-white/50">{selfEntry.pullCount} pack opens</p>
        </div>
      )}

      <div className="grid grid-cols-2 gap-2 lg:grid-cols-3">
        <Link to="/orders" className="glass-panel rounded-2xl p-3.5 transition-transform active:scale-[0.99] lg:hover:-translate-y-0.5">
          <Package size={20} className="text-white/70" />
          <p className="mt-1 text-xs font-semibold">My orders</p>
        </Link>
        {(moduleEnabled("ecommerce") || moduleEnabled("marketplace")) && (
          <Link to="/account/wishlist" className="glass-panel rounded-2xl p-3.5 transition-transform active:scale-[0.99] lg:hover:-translate-y-0.5">
            <Heart size={20} className="text-white/70" />
            <p className="mt-1 text-xs font-semibold">Wishlist</p>
          </Link>
        )}
        {moduleEnabled("collection") && (
          <Link to="/inventory" className="glass-panel rounded-2xl p-3.5 transition-transform active:scale-[0.99] lg:hover:-translate-y-0.5">
            <Archive size={20} className="text-white/70" />
            <p className="mt-1 text-xs font-semibold">Inventory</p>
          </Link>
        )}
        {moduleEnabled("instalments") && config?.flexxiEnabled && (
          <Link
            to="/account/flexxi"
            className="glass-panel rounded-2xl p-3.5 transition-transform active:scale-[0.99] lg:hover:-translate-y-0.5"
          >
            <CalendarClock size={20} className="text-white/70" />
            <p className="mt-1 text-xs font-semibold">My FlexxiPlans</p>
          </Link>
        )}
        {moduleEnabled("instalments") && config?.flexxiEnabled && (
          <Link
            to="/account/flexxi/verify"
            className="glass-panel rounded-2xl p-3.5 transition-transform active:scale-[0.99] lg:hover:-translate-y-0.5"
          >
            <FileText size={20} className="text-white/70" />
            <p className="mt-1 text-xs font-semibold">Flexxi verification</p>
          </Link>
        )}
        {config?.creditsEnabled && (
          <Link to="/wallet" className="glass-panel rounded-2xl p-3.5 transition-transform active:scale-[0.99] lg:hover:-translate-y-0.5">
            <Hexagon size={20} className="text-white/70" />
            <p className="mt-1 text-xs font-semibold">Wallet</p>
          </Link>
        )}
        <Link to="/account/addresses" className="glass-panel rounded-2xl p-3.5 transition-transform active:scale-[0.99] lg:hover:-translate-y-0.5">
          <MapPin size={20} className="text-white/70" />
          <p className="mt-1 text-xs font-semibold">Addresses</p>
        </Link>
        {moduleEnabled("support_forms") && (
          <Link to="/support" className="glass-panel rounded-2xl p-3.5 transition-transform active:scale-[0.99] lg:hover:-translate-y-0.5">
            <MessageCircle size={20} className="text-white/70" />
            <p className="mt-1 text-xs font-semibold">Support</p>
          </Link>
        )}
        <Link to="/account/settings" className="glass-panel rounded-2xl p-3.5 transition-transform active:scale-[0.99] lg:hover:-translate-y-0.5">
          <Settings size={20} className="text-white/70" />
          <p className="mt-1 text-xs font-semibold">Settings</p>
        </Link>
        {marketplaceOn && sellerPortalUrl ? (
          <button
            type="button"
            onClick={openSellerPortal}
            className="glass-panel rounded-2xl p-3.5 text-left transition-transform active:scale-[0.99] lg:hover:-translate-y-0.5"
          >
            <Store size={20} className="text-white/70" />
            <p className="mt-1 text-xs font-semibold">{isSeller ? "Seller portal" : "Become a seller"}</p>
          </button>
        ) : null}
      </div>

      <div className="space-y-2">
        <p className="text-[10px] font-semibold uppercase tracking-widest text-white/40">Legal</p>
        <div className="grid grid-cols-2 gap-2">
          {LEGAL_PAGES.map((page) => (
            <Link
              key={page.slug}
              to={`/legal/${page.slug}`}
              className="glass-panel flex items-center gap-2 rounded-2xl p-3.5 transition-transform active:scale-[0.99] lg:hover:-translate-y-0.5"
            >
              <FileText size={18} className="shrink-0 text-white/70" />
              <span className="text-xs font-semibold leading-snug">{page.title}</span>
            </Link>
          ))}
        </div>
      </div>

      <button type="button" onClick={() => void doLogout()} className="w-full py-2 text-center text-xs text-white/40 active:text-red-400">
        Log out
      </button>

      <p className="pb-4 text-center text-[10px] text-white/25">Mr Gaole · Collectors Platform</p>
    </div>
  );
}
